const SESSION_STORAGE_KEY = "west-marches:removed-campaign-notice";

export type RemovedCampaignNotice = {
  shortId: string;
  name: string;
};

function safeSessionStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

let inMemoryNotice: RemovedCampaignNotice | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Listener errors must never break the producer.
    }
  }
}

export function subscribeToRemovedCampaignNotice(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setRemovedCampaignNotice(notice: RemovedCampaignNotice): void {
  inMemoryNotice = notice;
  const storage = safeSessionStorage();
  if (storage) {
    try {
      storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(notice));
    } catch {
      // Swallow quota / privacy errors; in-memory copy still works.
    }
  }
  notify();
}

export function consumeRemovedCampaignNotice(): RemovedCampaignNotice | null {
  let notice: RemovedCampaignNotice | null = inMemoryNotice;
  inMemoryNotice = null;

  const storage = safeSessionStorage();
  if (storage) {
    try {
      const raw = storage.getItem(SESSION_STORAGE_KEY);
      storage.removeItem(SESSION_STORAGE_KEY);
      if (!notice && raw) {
        const parsed = JSON.parse(raw) as Partial<RemovedCampaignNotice> | null;
        if (parsed && typeof parsed.shortId === "string" && typeof parsed.name === "string") {
          notice = { shortId: parsed.shortId, name: parsed.name };
        }
      }
    } catch {
      // Ignore malformed payloads.
    }
  }

  return notice;
}

export function peekRemovedCampaignNotice(): RemovedCampaignNotice | null {
  if (inMemoryNotice) {
    return inMemoryNotice;
  }
  const storage = safeSessionStorage();
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<RemovedCampaignNotice> | null;
    if (parsed && typeof parsed.shortId === "string" && typeof parsed.name === "string") {
      return { shortId: parsed.shortId, name: parsed.name };
    }
  } catch {
    // Ignore.
  }
  return null;
}
